import React, { useEffect, useRef, useState } from "react"
import {
  Form,
  Modal,
  notification,
  Checkbox,
  Row,
  Col,
  Input,
} from "antd"
import _ from "lodash"
import { Tenant } from "../../models/tenant"
import { Checks, Settinglable } from "../../models/setting"
import { unwrapResult } from "@reduxjs/toolkit"
import { getSettingDetail, saveSettingLabel, selectSettings, selectTenant } from "../user/setting/settingSlice"
import { useDispatch, useSelector } from "react-redux"
import { RootDispatch } from "../../app/store"
import styles from './AnnualCapitalFund.module.css'

export const WXRTSettingModal = (props: {
  visible: any
  onOk: () => void
  onCancel: () => void
}) => {
  const dispatch = useDispatch<RootDispatch>()

  const settings = useSelector(selectSettings)

  const inputRef = useRef<any>(null)

  const [confirmLoading, setConfirmLoading] = useState(false)

  const [form] = Form.useForm()

  const tenant = useSelector(selectTenant)

  useEffect(() => {
    if (!props.visible) {
      form.resetFields()
    } else {
      // 解析城乡居民基本医疗保险设置
      const valueZG = settings?.[Checks.WXRTJMYL] || ''
      const [ruralEnable, ruralVisitCount, ruralAmount] = valueZG.split('#')
      
      // 解析职工医疗保险设置
      const valueJMYL = settings?.[Checks.WXRTZGYL] || ''
      const [workerEnable, workerVisitCount, workerAmount] = valueJMYL.split('#')
      
      // 初始化表单数据
      form.setFieldsValue({
        ruralEnable: ruralEnable === 'true',
        ruralVisitCount: ruralVisitCount || '',
        ruralAmount: ruralAmount || '',
        workerEnable: workerEnable === 'true',
        workerVisitCount: workerVisitCount || '',
        workerAmount: workerAmount || '',
      })
    }
  }, [props.visible])

  return (
    <>
      <Modal
        title={'无效人头设置'}
        width={800}
        confirmLoading={confirmLoading}
        visible={props?.visible}
        className={styles.modal}
        onCancel={() => {
          props?.onCancel()
        }}
        onOk={() => {
          form.submit()
        }}
        centered
        maskClosable={false}
      >
        <div style={{ height: 200, overflow: "auto" }}>
          <Form
            form={form}
            style={{ paddingRight: 10 }}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 19 }}
            onFinish={(values) => {
              // 合并表单字段为指定格式
              const mergedValues = {
                [Checks.WXRTJMYL]: `${values.ruralEnable}#${values.ruralVisitCount}#${values.ruralAmount}`,
                [Checks.WXRTZGYL]: `${values.workerEnable}#${values.workerVisitCount}#${values.workerAmount}`,
              }
              
              const pattern = /^[0-9]*$/

              const t = _.chain(mergedValues)
                .omitBy((_, k) => pattern.test(k))
                .value() as Tenant

              const f = _.chain(mergedValues)
                .pickBy((_, k) => pattern.test(k))
                .mapValues(
                  (v, k: string) =>
                  ({
                    type: k,
                    value: v?.toString(),
                  } as Settinglable)
                )
                .values()
                .value() as Settinglable[]
              
              setConfirmLoading(true)
              dispatch(
                saveSettingLabel({
                  tenant: {
                    ...tenant,
                    ...t,
                  },
                  functionList: f,
                })
              )
                .then(unwrapResult)
                .then(() => {
                  notification.success({
                    message: '保存成功',
                    duration: 3,
                  })
                  props?.onOk && props?.onOk()
                  dispatch(getSettingDetail())
                })
                .catch(() => {
                  notification.error({ message: '保存失败', duration: 3 })
                  dispatch(getSettingDetail())
                  props?.onCancel && props?.onCancel()
                })
                .finally(() => {
                  setConfirmLoading(false)
                })
            }}
          >
            {/* 城乡居民基本医疗保险 */}
            <div>
              <Form.Item name="ruralEnable" valuePropName="checked" label="城乡居民基本医疗保险">
                <Checkbox>开启无效人头</Checkbox>
              </Form.Item>
              <Row>
                <Col offset={5} span={19} style={{ gap: 8 }} className={styles.flexBaseline}>
                  <span>自然年就诊记录低于</span>
                  <Form.Item 
                    name="ruralVisitCount"
                    rules={[
                      {
                        validator: (_, value) => {
                          const ruralEnable = form.getFieldValue('ruralEnable')
                          if (ruralEnable && !value) {
                            return Promise.reject(new Error('请输入就诊记录次数'))
                          }
                          return Promise.resolve()
                        }
                      }
                    ]}
                  >
                    <Input
                      style={{ width: 80 }}
                      min={0}
                    />
                  </Form.Item>
                  <span>次，且统筹基金低于</span>
                  <Form.Item
                    name="ruralAmount"
                    rules={[
                      {
                        validator: (_, value) => {
                          const ruralEnable = form.getFieldValue('ruralEnable')
                          if (ruralEnable && !value) {
                            return Promise.reject(new Error('请输入统筹基金'))
                          }
                          return Promise.resolve()
                        }
                      }
                    ]}
                  >
                    <Input
                      style={{ width: 80 }}
                      min={0}
                      onChange={(e) => {
                        const v = e.target.value
                          .replace(/[^\d.]/g, '')
                          .replace(/^\./g, '')
                          .replace(/\.{2,}/g, '.')
                          .replace(/^(\d+\.?\d*).*$/, '$1')
                        form.setFieldsValue({ ruralAmount: v })
                      }}
                    />
                  </Form.Item>
                  <span>元算无效人头</span>
                </Col>
              </Row>
            </div>

            {/* 职工医疗保险 */}
            <div>
              <Form.Item name="workerEnable" valuePropName="checked" label="职工医疗保险">
                <Checkbox>开启无效人头</Checkbox>
              </Form.Item>
              <Row>
                <Col offset={5} span={19} style={{ gap: 8 }} className={styles.flexBaseline}>
                  <span>自然年就诊记录低于</span>
                  <Form.Item 
                    name="workerVisitCount"
                    rules={[
                      {
                        validator: (_, value) => {
                          const workerEnable = form.getFieldValue('workerEnable')
                          if (workerEnable && !value) {
                            return Promise.reject(new Error('请输入就诊记录次数'))
                          }
                          return Promise.resolve()
                        }
                      }
                    ]}
                  >
                    <Input
                      style={{ width: 80 }}
                      min={0}
                    />
                  </Form.Item>
                  <span>次，且统筹基金低于</span>
                  <Form.Item
                    name="workerAmount"
                    rules={[
                      {
                        validator: (_, value) => {
                          const workerEnable = form.getFieldValue('workerEnable')
                          if (workerEnable && !value) {
                            return Promise.reject(new Error('请输入统筹基金'))
                          }
                          return Promise.resolve()
                        }
                      }
                    ]}
                  >
                    <Input
                      style={{ width: 80 }}
                      min={0}
                      onChange={(e) => {
                        const v = e.target.value
                          .replace(/[^\d.]/g, '')
                          .replace(/^\./g, '')
                          .replace(/\.{2,}/g, '.')
                          .replace(/^(\d+\.?\d*).*$/, '$1')
                        form.setFieldsValue({ workerAmount: v })
                      }}
                    />
                  </Form.Item>
                  <span>元算无效人头</span>
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      </Modal>
    </>
  )
}
